import { faCheck, faFileLines } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import { useUpdateBlueprintFormSchema } from '@/hooks/useBlueprint';
import { Button, Modal } from '@/ui';

import FormRenderer from '../renderer/FormRenderer';
import type { FieldDef, FormSchema } from '../types';

import AddToLibraryDialog from './AddToLibraryDialog';
import BuilderCanvas from './BuilderCanvas';
import FieldSettingsPanel from './FieldSettingsPanel';
import LibraryPanel from './LibraryPanel';
import SaveSectionDialog from './SaveSectionDialog';
import useBuilderState from './useBuilderState';

interface FormBuilderProps {
  blueprintId: string;
  blueprintName: string;
  initialSchema?: FormSchema | null;
}

const PreviewModal = ({ schema, onClose }: { schema: FormSchema; onClose: () => void }) => {
  const { control } = useForm();
  return (
    <Modal open onClose={onClose} title="Form preview" size="lg">
      <FormRenderer schema={schema} control={control} namespace="preview" />
    </Modal>
  );
};

const FormBuilder = ({ blueprintId, blueprintName, initialSchema }: FormBuilderProps) => {
  const state = useBuilderState(initialSchema);
  const updateSchema = useUpdateBlueprintFormSchema(blueprintId);

  const [reusableField, setReusableField] = useState<FieldDef | null>(null);
  const [saveSectionKey, setSaveSectionKey] = useState<string | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [justSaved, setJustSaved] = useState(false);

  // The confirmation is otherwise easy to miss next to a button that's
  // already back to its resting state, so it gets its own timed fade rather
  // than tying its visibility to isPending.
  useEffect(() => {
    if (!justSaved) return;
    const timeout = setTimeout(() => setJustSaved(false), 3000);
    return () => clearTimeout(timeout);
  }, [justSaved]);

  const onToggleReusable = (field: FieldDef) => {
    if (field.inLibrary) {
      // Unstarring detaches from the shared question entirely, so the next save
      // mints the form its own private one.
      state.updateField(field.key, { questionId: undefined, inLibrary: undefined });
    } else {
      setReusableField(field);
    }
  };

  // The backend rejects a schema it can't store — a field placed in a section it
  // doesn't own, a key that clashes — and the reason is the only clue the builder
  // can give, so it goes on screen rather than only into the console.
  const save = () => {
    setSaveError(null);
    updateSchema.mutate(state.schema, {
      onSuccess: () => {
        state.markSaved();
        setJustSaved(true);
      },
      onError: (error) => setSaveError(error.message)
    });
  };

  const sectionFields = (key: string) => state.fields.filter((f) => f.sectionKey === key);
  const saveSection = state.sections.find((s) => s.key === saveSectionKey);
  const activeSection = state.sections.find((s) => s.key === state.activeSectionKey);

  return (
    <div
      className="overflow-hidden rounded-2xl border border-uniform-gray-200 bg-white shadow-xl dark:border-uniform-gray-700 dark:bg-uniform-gray-900"
      data-testid="form-builder">
      <div className="flex items-center justify-between border-b border-uniform-gray-100 px-6 py-[18px] dark:border-uniform-gray-700">
        <div className="flex min-w-0 items-center gap-3">
          <div className="flex h-[34px] w-[34px] flex-none items-center justify-center rounded-lg bg-blue-50 text-blue-600 dark:bg-blue-950/30 dark:text-blue-300">
            <FontAwesomeIcon icon={faFileLines} />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-base font-bold text-uniform-gray-900 dark:text-uniform-gray-100">
                {blueprintName}
              </span>
              {state.isDirty && (
                <span className="rounded-md bg-uniform-gray-100 px-1.5 py-0.5 text-[11px] font-semibold text-uniform-gray-500 dark:bg-uniform-gray-800 dark:text-uniform-gray-400">
                  Unsaved
                </span>
              )}
              {!state.isDirty && justSaved && (
                <span className="flex items-center gap-1 rounded-md bg-green-50 px-1.5 py-0.5 text-[11px] font-semibold text-green-700 dark:bg-green-950/30 dark:text-green-300">
                  <FontAwesomeIcon icon={faCheck} className="text-[10px]" />
                  Saved
                </span>
              )}
            </div>
            <div className="text-[12.5px] text-uniform-gray-500 dark:text-uniform-gray-400">
              {state.fields.length} fields · {state.sections.length} sections
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2.5">
          <Button size="sm" color="neutral" variant="outline" onClick={() => setPreviewing(true)}>
            Preview
          </Button>
          <Button size="sm" color="primary" isLoading={updateSchema.isPending} onClick={save}>
            Save form
          </Button>
        </div>
      </div>

      {saveError && (
        <p
          role="alert"
          className="border-b border-red-100 bg-red-50 px-6 py-2.5 text-[12.5px] leading-relaxed text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-300">
          {saveError}
        </p>
      )}

      <div className="grid h-[854px]" style={{ gridTemplateColumns: '322px minmax(0,1fr) 356px' }}>
        <LibraryPanel
          // A new field or question joins whichever section the canvas has
          // targeted, so a section can be built out from the library directly.
          onAddField={(widget) => state.addField(widget, state.activeSectionKey ?? undefined)}
          onInsertQuestion={(question) =>
            state.insertQuestion(question, state.activeSectionKey ?? undefined)
          }
          onInsertSection={state.insertSection}
          usedKeys={new Set(state.fields.map((f) => f.key))}
          targetSectionTitle={activeSection && (activeSection.title || activeSection.key)}
          onClearTarget={() => state.targetSection(null)}
        />
        <BuilderCanvas
          state={state}
          onGroup={() => state.groupChecked('New section')}
          onSaveSection={setSaveSectionKey}
          onToggleReusable={onToggleReusable}
        />
        <FieldSettingsPanel state={state} onToggleReusable={onToggleReusable} />
      </div>

      {reusableField && (
        <AddToLibraryDialog
          field={reusableField}
          onClose={() => setReusableField(null)}
          onSaved={(questionId) =>
            state.updateField(reusableField.key, { questionId, inLibrary: true })
          }
        />
      )}

      {saveSection && (
        <SaveSectionDialog
          sectionTitle={saveSection.title || saveSection.key}
          fields={sectionFields(saveSection.key)}
          onClose={() => setSaveSectionKey(null)}
          onSaved={(sectionId, questionIdsByFieldKey) => {
            state.linkSection(saveSection.key, sectionId);
            Object.entries(questionIdsByFieldKey).forEach(([fieldKey, questionId]) =>
              state.updateField(fieldKey, { questionId, inLibrary: true })
            );
          }}
        />
      )}

      {previewing && <PreviewModal schema={state.schema} onClose={() => setPreviewing(false)} />}
    </div>
  );
};

export default FormBuilder;
